<script>
  import content from "./content";
  import grid from "./grid";
  import Element from "./element.svelte";
  import Widget from "./widget.svelte";
  // A .svelte file may import itself for recursion (replaces <svelte:self>).
  import Component from "./component.svelte";

  // component.svelte

  /**
   * @type {{
   *   def: import("./definition").definition,
   *   dataSet?: object,
   *   place?: import("./web").place,
   *   children?: import("svelte").Snippet,
   * }}
   */
  let { def, dataSet = {}, place = {}, children } = $props();

  // Where this part's children are drawn: inside it and its styles as its row fills them, within what holds it, on the same page.
  /** @param {number} num */
  const insideOf = (num) => ({
    ...place,
    within: def.t,
    styles: def.p?.styles,
    data: drawnFrom(num),
    around: place.styles,
    aroundData: place.data,
    first: false,
  });

  // If the component is fetching a list of data then each item within the
  // list needs to be provided the single row of data which matches its
  // index in the list.
  const isList = $derived(!!(def && def.d && def.d.v && def.d.v !== ""));

  // Rows fetched for a list view (empty for a normal component).
  /** @type {object[]} */
  let rows = $state([]);

  // For list components, subscribe to the view store. Cleaned up on destroy /
  // dependency change (the original Svelte 4 code never unsubscribed).
  $effect(() => {
    if (!isList) {
      return;
    }

    /** @type {(() => void) | undefined} */
    let unsubscribe;
    content.fetch(def.d.v).then((viewStore) => {
      unsubscribe = viewStore.subscribe((data) => {
        if (data && Array.isArray(data)) {
          rows = [...data];
        }
      });
    });

    return () => unsubscribe?.();
  });

  // One render slot per row for a list, otherwise a single slot. A list with
  // no rows yet renders nothing until its data resolves.
  const slots = $derived(isList ? rows.map((_, index) => index) : [0]);

  // The site's facts, in before the page is drawn and redrawn if kept anew.
  let site = $state(content.facts());
  $effect(() =>
    content.watchFacts((facts) => {
      site = facts;
    }),
  );

  // The row a slot is drawn from, with the site's facts under site, so ${site.phone} fills a list's items as it does any part.
  /** @param {number} num */
  const drawnFrom = (num) => ({
    ...(isList ? rows[num] : dataSet),
    site,
  });

  // The field of its row a part shows as its parts, a rich text, or none.
  const shownField = $derived(
    typeof def?.d?.f === "string" && def.d.f !== "" ? def.d.f : "",
  );

  /**
   * The parts drawn inside a slot: the part's own, or the rich text its row holds in the field it shows, as parts drawn like
   * any other, words written plainly there as one text.
   * @param {number} num
   * @returns {import("./definition").definition[] | undefined}
   */
  const kidsOf = (num) => {
    if (shownField === "") {
      return def.c;
    }
    const row = /** @type {Record<string, unknown>} */ (
      (isList ? rows[num] : dataSet) ?? {}
    );
    const value = row[shownField];
    if (Array.isArray(value)) {
      return value.filter((part) => part && typeof part === "object");
    }
    if (typeof value === "string") {
      return [
        /** @type {import("./definition").definition} */ ({
          t: "text",
          p: { txt: value },
        }),
      ];
    }
    return [];
  };

  // A scoped style, not Tailwind classes: any grid-template-columns value draws as written, with or without the CDN.
  const uid = $props.id();
  const scope = `stenzil-grid-${uid}`;

  const isGrid = $derived(def?.t === "grid");

  // The style a grid draws under its own class, or none for any other part.
  const gridStyle = $derived(
    isGrid ? grid.styleOf(scope, def.p, def.c ?? []) : "",
  );

  // The props a part is drawn in, a grid's wearing its own class too.
  const drawnProps = $derived(
    isGrid
      ? { ...def.p, styles: `${def.p?.styles ?? ""} ${scope}`.trim() }
      : def.p,
  );

  /**
   * A grid's child wearing its cell's class; a widget sits in a box that wears it, as a call's styles replace the widget's own.
   * @param {import("./definition").definition} child
   * @param {number} index
   * @returns {import("./definition").definition}
   */
  const inCell = (child, index) => {
    if (!isGrid || !child?.p?.cell) {
      return child;
    }
    const styles = grid.cellClass(scope, index);
    if (child.t === "widget") {
      return /** @type {import("./definition").definition} */ ({
        t: "box",
        p: { styles },
        c: [child],
      });
    }
    return {
      ...child,
      p: { ...child.p, styles: `${child.p.styles ?? ""} ${styles}`.trim() },
    };
  };
</script>

<svelte:head>
  {#if gridStyle}
    <svelte:element this={"style"}>{gridStyle}</svelte:element>
  {/if}
</svelte:head>

{#if def.t === "widget"}
  <Widget {def} {place} />
{:else}
  {#each slots as num (num)}
    <Element
      name={def.t}
      props={drawnProps}
      data={drawnFrom(num)}
      {place}
      holds={children || shownField !== "" ? undefined : def.c}
    >
      {#if children}
        {@render children()}
      {:else if kidsOf(num)}
        {#each kidsOf(num) ?? [] as child, index}
          <Component
            def={inCell(child, index)}
            dataSet={isList ? rows[num] : dataSet}
            place={index === 0
              ? { ...insideOf(num), first: true }
              : insideOf(num)}
          />
        {/each}
      {/if}
    </Element>
  {/each}
{/if}
